<template>
  <div class="system-user-individual-modal">
    <cs-form-dialog
      id="dlg-system-user-individual-modal"
      v-model="systemUserIndividualModalDisplayed"
      body-id="dlg-body-system-user-individual-modal"
      heading="System User Individual Modal"
      :primary-action="{
        label: 'Save',
        eventName: 'primary-click',
        loading: systemUserSaveActionInProgress,
        disabled: systemUserSaveActionInProgress || v$.$invalid
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click',
        loading: systemUserSaveActionInProgress
      }"
      @primary-click="save"
      @secondary1-click="close"
    >
      <template #cs-form-dialog-content>
        <v-container grid-list-md class="form-container">
          <v-row no-gutters class="system-user-individual-modal-row-with-error">
            <v-col cols="12"> Email </v-col>
            <v-col cols="12">
              <v-text-field
                id="system-user-individual-modal--email"
                v-model="email"
                :disabled="isHubManagedUser"
                variant="solo"
                required
                :error-messages="emailErrors"
                density="compact"
                class="excursion-input"
                placeholder="Enter in the email of the user"
                @keydown.space.prevent
                @update:model-value="v$.email.$touch()"
                @blur="v$.email.$touch()"
              />
            </v-col>
          </v-row>
          <v-row no-gutters class="system-user-individual-modal-row">
            <v-col cols="12" class="mt-2"> Given Name </v-col>
            <v-col cols="12">
              <v-text-field
                id="system-user-individual-modal--given-name"
                v-model="givenName"
                :disabled="isHubManagedUser"
                variant="solo"
                density="compact"
                class="excursion-input"
                placeholder="Enter in the given name of the user"
                hide-details
              />
            </v-col>
          </v-row>
          <v-row no-gutters class="system-user-individual-modal-row">
            <v-col cols="12" class="mt-1"> Surname </v-col>
            <v-col cols="12">
              <v-text-field
                id="system-user-individual-modal--surname"
                v-model="surname"
                :disabled="isHubManagedUser"
                variant="solo"
                density="compact"
                class="excursion-input"
                placeholder="Enter in the surname of the user"
                hide-details
              />
            </v-col>
          </v-row>
          <v-row no-gutters class="system-user-individual-modal-row">
            <v-col cols="12" class="mt-1"> System ACL Role </v-col>
            <v-col cols="12">
              <v-select
                id="system-user-individual-modal--system-acl-role"
                v-model="systemAclRole"
                class="type-dropdown excursion-selection"
                placeholder="Select System ACL Role"
                :items="getSystemAclRoles()"
                density="compact"
                required
                :error-messages="systemAclRoleErrors"
                variant="solo"
                color="primary"
                hide-details
                @update:model-value="v$.systemAclRole.$touch()"
                @blur="v$.systemAclRole.$touch()"
              >
                <template #item="data">
                  <v-list-item
                    v-bind="data.props"
                    :id="`system-user-individual-modal--system-acl-role-item-${data.item}`"
                  ></v-list-item>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row no-gutters class="system-user-individual-modal-row">
            <v-col cols="12" class="mt-2"> Client ACL Role </v-col>
            <v-col cols="12">
              <v-select
                id="system-user-individual-modal--client-acl-role"
                v-model="clientAclRole"
                class="type-dropdown excursion-selection"
                placeholder="Select Client ACL Role"
                :items="clientAclRoles"
                item-title="text"
                item-value="value"
                variant="solo"
                density="compact"
                color="primary"
                clear-icon="mdi-close"
                clearable
                hide-details
              >
                <template #item="data">
                  <v-list-item
                    v-bind="data.props"
                    :id="`system-user-individual-modal--system-acl-role-item-${data.item.text}`"
                  ></v-list-item>
                </template>
              </v-select>
            </v-col>
            <v-col v-if="clientAclRoleRequired" cols="12">
              <span class="pre-error"> Client ACL Role is required </span>
            </v-col>
          </v-row>
          <div v-if="inferiorRoleSetupDisplayed" class="system-user-individual-modal-row">
            <div v-if="clientAdminAssistantRoleDisplaySetupEnabled" class="d-flex align-center">
              <div>
                <v-checkbox-btn
                  id="user-client-admin-assistant-role-display-enabled-tickbox"
                  v-model="clientAdminAssistantRoleDisplayEnabled"
                  hide-details
                  color="primary"
                ></v-checkbox-btn>
              </div>
              <span for="user-client-admin-assistant-role-display-enabled-tickbox">
                Display as <b>client-admin-assistant</b>
              </span>
            </div>
            <div v-if="clientApproverRoleDisplaySetupEnabled" class="d-flex align-center">
              <div>
                <v-checkbox-btn
                  id="user-client-approver-role-display-enabled-tickbox"
                  v-model="clientApproverRoleDisplayEnabled"
                  hide-details
                  color="primary"
                ></v-checkbox-btn>
              </div>
              <span for="user-client-approver-role-display-enabled-tickbox">
                Display as <b>client-approver</b>
              </span>
            </div>
            <div v-if="clientUserRoleDisplaySetupEnabled" class="d-flex align-center">
              <div>
                <v-checkbox-btn
                  id="user-client-user-role-display-enabled-tickbox"
                  v-model="clientUserRoleDisplayEnabled"
                  hide-details
                  color="primary"
                ></v-checkbox-btn>
              </div>
              <span for="user-client-user-role-display-enabled-tickbox">
                Display as <b>client-user</b>
              </span>
            </div>
          </div>
          <v-row no-gutters class="system-user-individual-modal-row">
            <v-col cols="12"> Clients </v-col>
            <v-col v-if="hubClients.length > 0" cols="12" class="client-section">
              <div class="client-section-label">Hub-assigned</div>
              <div id="system-user-individual-modal--hub-clients" class="hub-chip-list">
                <v-chip
                  v-for="hubClient in hubClients"
                  :id="`system-user-individual-modal--hub-client-chip-${hubClient.sid}`"
                  :key="hubClient.sid"
                  color="primary"
                  variant="tonal"
                  size="default"
                  class="hub-client-chip"
                  aria-readonly="true"
                  :title="hubChipTooltip"
                  tabindex="-1"
                >
                  <span class="hub-client-chip__dot"></span>
                  <span class="hub-client-chip__label">Hub</span>
                  <span class="hub-client-chip__name">{{ hubClient.name }}</span>
                  <span class="visually-hidden">Hub-assigned, not editable</span>
                  <v-tooltip activator="parent" location="top">
                    {{ hubChipTooltip }}
                  </v-tooltip>
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" class="client-section">
              <div class="client-section-label">Manually assigned</div>
              <v-autocomplete
                id="system-user-individual-modal--clients"
                ref="selectClient"
                v-model="manualClients"
                v-model:search="searchClients"
                :items="clientItems"
                label="Select one or more Clients"
                item-title="name"
                item-value="sid"
                density="compact"
                clearable
                color="primary"
                single-line
                multiple
                chips
                hide-details
                clear-icon="mdi-close"
                :hide-selected="hideSelected"
                closable-chips
                variant="solo"
                return-object
                @update:model-value="handleClientChange"
                @focus="handleClientFocus"
                @close="handleClientClose"
              >
                <template #item="data">
                  <v-list-item
                    v-bind="data.props"
                    :id="`system-user-individual-modal--clients-item-${data.item.sid}`"
                  />
                </template>
              </v-autocomplete>
              <div v-if="clientsRequired" class="pre-error">Clients are required</div>
            </v-col>
          </v-row>
        </v-container>
        <v-divider></v-divider>
      </template>
    </cs-form-dialog>
    <cs-form-dialog
      v-model="genericDialogDisplayed"
      body-id="dlg-body-system-user-individual-modal-generic-msg"
      heading="System User Individual Modal"
      :primary-action="{ label: 'OK', eventName: 'primary-click' }"
      @primary-click="genericDialogDisplayed = false"
    >
      <template #cs-form-dialog-content>
        {{ genericDialogMessage }}
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import * as types from '@/store/mutationTypes';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import aclRole from '../lib/const/aclRole';

const HUB_CHIP_TOOLTIP = 'Assigned by Hub.';

export default {
  name: 'SystemUserIndividualModal',
  validations() {
    return {
      email: {
        required
      },
      systemAclRole: {
        required
      }
    };
  },
  props: {
    propSystemUserIndividualModalRefreshed: {
      type: Boolean,
      default: false
    },
    propUserId: {
      type: String,
      required: false,
      default: ''
    }
  },

  emits: ['reloadUsers'],

  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      genericDialogDisplayed: false,
      systemUserSaveActionInProgress: false,
      genericDialogMessage: '',
      systemUserIndividualModalDisplayed: false,
      selectedClients: [],
      searchClients: '',
      clientAclRoles: [
        { value: aclRole.CLIENT_ADMIN, text: aclRole.CLIENT_ADMIN },
        {
          value: aclRole.CLIENT_ADMIN_ASSISTANT,
          text: aclRole.CLIENT_ADMIN_ASSISTANT
        },
        { value: aclRole.CLIENT_APPROVER, text: aclRole.CLIENT_APPROVER },
        { value: aclRole.CLIENT_GROUP_ADMIN, text: aclRole.CLIENT_GROUP_ADMIN },
        {
          value: aclRole.CLIENT_GROUP_APPROVER,
          text: aclRole.CLIENT_GROUP_APPROVER
        },
        { value: aclRole.CLIENT_GROUP_USER, text: aclRole.CLIENT_GROUP_USER },
        {
          value: aclRole.CLIENT_GROUP_WATCHER,
          text: aclRole.CLIENT_GROUP_WATCHER
        },
        { value: aclRole.CLIENT_LITE_USER, text: aclRole.CLIENT_LITE_USER },
        { value: aclRole.CLIENT_USER, text: aclRole.CLIENT_USER },
        { value: aclRole.CLIENT_WATCHER, text: aclRole.CLIENT_WATCHER }
      ],
      pagination: {
        rowsPerPage: -1,
        descending: false,
        sortBy: '',
        page: 1,
        order: ''
      },
      clientAclRole: null,
      systemAclRole: null,
      email: '',
      emailConflicted: false,
      emailValid: true,
      clientAclRoleRequired: false,
      clientsRequired: false,
      givenName: '',
      surname: '',
      clientAdminAssistantRoleDisplayEnabled: false,
      clientApproverRoleDisplayEnabled: false,
      clientUserRoleDisplayEnabled: false,
      hideSelected: false
    };
  },
  computed: {
    isHubEnabled() {
      return this.$store.getters['authorisation/hubEnabled'];
    },
    isHubManagedUser() {
      return this.isHubEnabled && this.hubClients.length > 0;
    },
    systemUserIndividualModalRefreshed() {
      return this.propSystemUserIndividualModalRefreshed;
    },
    clientCache() {
      return this.$store.state.common.parentClientList.itemsCache;
    },
    hubChipTooltip() {
      return HUB_CHIP_TOOLTIP;
    },
    clientPartition() {
      return this.$store.getters['common/partitionUserClients'](this.selectedClients);
    },
    hubClients() {
      return this.clientPartition.hubClients;
    },
    manualClients: {
      get() {
        return this.clientPartition.manualClients;
      },
      set(newManualClients) {
        const manuals = Array.isArray(newManualClients) ? newManualClients : [];
        this.selectedClients = [...this.hubClients, ...manuals];
      }
    },
    clientItems() {
      const { items } = this.$store.state.common.parentClientList;
      this.manualClients.forEach((client) => {
        if (!items.some((e) => e.sid === client.sid)) {
          this.$store.commit(types.COMMON_ADD_PARENT_CLIENT_LIST, client);
        }
      });
      const hubSids = new Set(this.hubClients.map((c) => c.sid));
      return items.filter((item) => !hubSids.has(item.sid));
    },
    emailErrors() {
      const errors = [];
      if (!this.v$.email.$dirty) return errors;
      if (this.v$.email.required.$invalid) {
        errors.push('Email is required');
      }

      if (!this.emailValid) {
        errors.push('Invalid Email');
      }

      if (this.emailConflicted) {
        errors.push('Email conflicted');
      }

      return errors;
    },
    systemAclRoleErrors() {
      const errors = [];
      if (!this.v$.systemAclRole.$dirty) return errors;
      if (this.v$.systemAclRole.required.$invalid) {
        errors.push('System ACL Role is required');
      }

      return errors;
    },
    systemAdminSelectionOptionDisplayed() {
      return this.$store.state.common.systemAdminSelectionOptionDisplayed;
    },
    clientAdminAssistantRoleDisplaySetupEnabled() {
      return this.checkIfInferiorRoleDisplaySetupEnabled(
        this.clientAclRole,
        aclRole.CLIENT_ADMIN_ASSISTANT
      );
    },
    clientApproverRoleDisplaySetupEnabled() {
      return this.checkIfInferiorRoleDisplaySetupEnabled(
        this.clientAclRole,
        aclRole.CLIENT_APPROVER
      );
    },
    clientUserRoleDisplaySetupEnabled() {
      return this.checkIfInferiorRoleDisplaySetupEnabled(this.clientAclRole, aclRole.CLIENT_USER);
    },
    inferiorRoleSetupDisplayed() {
      return (
        this.clientAdminAssistantRoleDisplaySetupEnabled ||
        this.clientApproverRoleDisplaySetupEnabled ||
        this.clientUserRoleDisplaySetupEnabled
      );
    }
  },
  watch: {
    clientAclRole() {
      if (this.clientAclRole) {
        this.clientAclRoleRequired = false;
      }
    },
    selectedClients() {
      if (this.selectedClients && this.selectedClients.length > 0) {
        this.clientsRequired = false;
      }
    },
    systemUserIndividualModalRefreshed() {
      this.newUserModal(this.propUserId);
      this.systemUserIndividualModalDisplayed = true;
    },
    async searchClients(val) {
      await this.filterClients(val);
    },
    systemUserIndividualModalDisplayed(newState) {
      if (newState === false) {
        this.clear();
      }
    }
  },
  async mounted() {
    this.systemUserIndividualModalDisplayed = true;
    await this.newUserModal(this.propUserId);
    await this.filterClients('');
  },
  methods: {
    alertSaveResult(cbResponse) {
      this.genericDialogDisplayed = true;
      this.systemUserSaveActionInProgress = false;
      this.clientAclRoleRequired = false;
      this.close();
      this.clear();
      if (cbResponse && cbResponse.data) {
        // success
        this.genericDialogMessage = 'User has been saved.';

        this.$emit('reloadUsers');
      } else {
        // failed
        this.genericDialogMessage = 'Failed to save User information.';
      }
    },
    clear() {
      this.v$.$reset();
      this.clientAclRole = null;
      this.email = '';
      this.systemAclRole = null;
      this.emailValid = true;
      this.givenName = '';
      this.surname = '';
      this.clientAdminAssistantRoleDisplayEnabled = false;
      this.clientApproverRoleDisplayEnabled = false;
      this.clientUserRoleDisplayEnabled = false;
      this.clientsRequired = false;
      this.clientAclRoleRequired = false;
      this.selectedClients = [];
    },
    close() {
      this.systemUserIndividualModalDisplayed = false;
      this.clear();
    },
    initSelectClient() {
      if (this.$refs.selectClient === undefined) {
        return;
      }
      // Initialize the v-autocomplete hide-selected attribute
      this.$refs.selectClient.click();
      this.$nextTick(() => {
        this.$refs.selectClient.blur();
      });
    },
    handleClientChange() {
      this.$refs.selectClient.blur();
      this.$nextTick(() => {
        this.$refs.selectClient.click();
      });
    },
    handleClientFocus() {
      this.hideSelected = true;
    },
    handleClientClose() {
      this.hideSelected = false;
    },
    async newUserModal(id) {
      if (`${id}` !== '') {
        const argHash = {
          id,
          store: this.$store
        };

        const cbResponse = await this.$app.stbApiAdapter.getUsers(argHash);
        if (cbResponse && !cbResponse.error) {
          const user = cbResponse.data[0];
          this.email = user.email;
          this.systemAclRole = user['acl-role'];
          const userMetadata = user.metadata;

          if (userMetadata) {
            this.givenName = userMetadata.givenName;
            this.surname = userMetadata.surname;
            this.clientAdminAssistantRoleDisplayEnabled =
              userMetadata.clientAdminAssistantRoleDisplayEnabled;
            this.clientApproverRoleDisplayEnabled = userMetadata.clientApproverRoleDisplayEnabled;
            this.clientUserRoleDisplayEnabled = userMetadata.clientUserRoleDisplayEnabled;
          } else {
            this.givenName = '';
            this.surname = '';
            this.clientAdminAssistantRoleDisplayEnabled = false;
            this.clientApproverRoleDisplayEnabled = false;
            this.clientUserRoleDisplayEnabled = false;
          }

          if (user.clientList && user.clientList.length > 0) {
            this.selectedClients = user.clientList.map((item) => {
              return {
                sid: item.sid,
                name: `${item.sid} - ${item.metadata.name}`,
                isHubAssigned: item.isHubAssigned === true,
                'acl-role': item['acl-role']
              };
            });
            const firstClient = this.selectedClients[0];
            if (firstClient) {
              this.clientAclRole = firstClient['acl-role'];
            }
          }
        }
        this.initSelectClient();
      } else {
        this.clear();
      }
    },
    async save() {
      this.emailValid = true;
      const regExp =
        /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

      if (!regExp.test(this.email)) {
        this.emailValid = false;
        return;
      }

      if ((!this.selectedClients || this.selectedClients.length <= 0) && this.clientAclRole) {
        this.clientsRequired = true;
        return;
      }

      if (this.selectedClients && this.selectedClients.length > 0 && !this.clientAclRole) {
        this.clientAclRoleRequired = true;
        return;
      }

      this.systemUserSaveActionInProgress = true;
      let clients = [];

      if (this.selectedClients && this.selectedClients.length > 0) {
        clients = this.selectedClients.map((item) => {
          return {
            sid: item.sid,
            'acl-role': this.clientAclRole
          };
        });
      }

      const apiParam = JSON.parse(JSON.stringify(this.$store.state.common.apiParam));
      const user = {
        'acl-role': this.systemAclRole,
        email: String(this.email).trim(),
        metadata: {
          givenName: String(this.givenName).trim(),
          surname: String(this.surname).trim(),
          clientAdminAssistantRoleDisplayEnabled: this.clientAdminAssistantRoleDisplayEnabled,
          clientApproverRoleDisplayEnabled: this.clientApproverRoleDisplayEnabled,
          clientUserRoleDisplayEnabled: this.clientUserRoleDisplayEnabled
        },
        clients
      };

      apiParam.user = user;

      if (`${this.propUserId}` !== '') {
        const argHash = {
          id: this.propUserId,
          payload: apiParam,
          store: this.$store
        };
        const cbResponse = await this.$app.stbApiAdapter.putUsers(argHash);
        this.systemUserSaveActionInProgress = false;
        if (`${cbResponse.statusCode}` === '409') {
          this.emailConflicted = true;
        } else {
          this.emailConflicted = false;
          this.alertSaveResult(cbResponse);
        }
      } else {
        const argHash = {
          payload: apiParam,
          store: this.$store
        };
        const cbResponse = await this.$app.stbApiAdapter.postUsers(argHash);
        this.systemUserSaveActionInProgress = false;
        if (`${cbResponse.statusCode}` === '409') {
          this.emailConflicted = true;
        } else {
          this.emailConflicted = false;
          this.alertSaveResult(cbResponse);
        }
      }
    },
    getSystemAclRoles() {
      const systemAclRoles = [
        aclRole.SYSTEM_GUEST,
        aclRole.SYSTEM_OPS_ADMIN,
        aclRole.SYSTEM_USER,
        aclRole.SYSTEM_WATCHER
      ];

      if (this.systemAdminSelectionOptionDisplayed) {
        systemAclRoles.splice(0, 0, aclRole.SYSTEM_ADMIN);
      }

      return systemAclRoles;
    },
    checkIfInferiorRoleDisplaySetupEnabled(superiorClientAclRole, inferiorClientAclRole) {
      let inferiorRoleDisplaySetupEnabled = false;

      if (superiorClientAclRole) {
        const inferiorClientAclRoleHash =
          this.$store.state.common.clientAclRoleInferiorityHash[superiorClientAclRole];

        inferiorRoleDisplaySetupEnabled =
          inferiorClientAclRoleHash && inferiorClientAclRoleHash[inferiorClientAclRole];
      }

      return inferiorRoleDisplaySetupEnabled;
    },
    async filterClients(val) {
      let cacheItem;
      const localVal = val || '';
      if (this.clientCache && this.clientCache.length > 0) {
        cacheItem = this.clientCache.find((item) => {
          return item.key === localVal;
        });
      }

      if (cacheItem) {
        this.$store.commit(types.COMMON_SET_PARENT_CLIENT_ITEMS, cacheItem.items);
      } else {
        this.$store.commit(types.COMMON_SET_PARENT_CLIENT_LIST_LOADING, true);
        const filterParam = {
          s: localVal,
          page: this.pagination.page,
          order: this.pagination.sortBy,
          length: 5,
          direction: this.pagination.descending === false ? 'ASC' : 'DESC'
        };

        Object.keys(filterParam).forEach((key) => {
          if (filterParam[key] === '') {
            delete filterParam[key];
          }
        });
        const argHash = {
          stbApiAdapter: this.$app.stbApiAdapter,
          filterParam,
          store: this.$store
        };
        await this.$store.dispatch('common/getParentClientList', argHash);
      }
    }
  }
};
</script>

<style scoped>
.form-container {
  font-size: 14px;
}

.pre-error {
  color: #ff5252;
  font-size: 12px;
}

.system-user-individual-modal-row {
  margin-bottom: 20px;
}

.system-user-individual-modal-row-with-error {
  margin-bottom: 0px;
}

.client-acl-role-helper {
  color: #5a5a5a;
  font-size: 12px;
}

.client-section {
  margin-top: 8px;
}

.client-section-label {
  font-size: 12px;
  color: #5a5a5a;
  margin-bottom: 4px;
}

.hub-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.hub-client-chip__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
  flex-shrink: 0;
  background: currentColor;
}

.hub-client-chip__label {
  font-weight: 600;
  margin-right: 6px;
}

.hub-client-chip__name {
  font-weight: 400;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
